import {
  CONTACT_EMAIL,
  FAV_ICON_194_X_194,
  PRODUCTION_HOST,
} from '../../../app/utilities/config'
import {
  translate,
  LANGUAGE_CODE_FRENCH,
  LANGUAGE_CODE_KOREAN,
  LANGUAGE_CODE_SPANISH,
  LANGUAGE_CODE_ENGLISH,
} from '../../../app/utilities/language'
import {
  SPAIN_ISO_3166_ALPHA_2_CODE,
  FRANCE_ISO_3166_ALPHA_2_CODE,
  SOUTH_KOREA_ISO_3166_ALPHA_2_CODE,
  UNITED_STATES_ISO_3166_ALPHA_2_CODE,
} from '../../../app/utilities/misc'
import {
  S_DEBUG_MODE,
  BLOCK_INSECURE_SITES,
  GOOGLE_WEB_RISK_API_KEY,
} from './config'
import {
  resolveIconUrl,
  toTrimmedString,
  isNullOrEmptyString,
  isNotNullOrEmptyString,
} from './misc'

export const TEST_NULL_BRAND_STUDIO_AI_API_RESPONSES: boolean = false
export const TEST_FALLBACK_TO_META_INFO_AI_API_RESPONSES: boolean =
  !TEST_NULL_BRAND_STUDIO_AI_API_RESPONSES && false
export const TEST_FALLBACK_TO_META_FAVICON: string = FAV_ICON_194_X_194
export const BRAND_STUDIO_EXPLAINER_VIDEO_AVAILABLE: boolean = false

const USER_AGENT_BY_DOMAIN: Map<string, string> = new Map<string, string>([
  ['youtube.com', 'Googlebot/2.1 (+http://www.google.com/bot.html)'],
  ['twitter.com', 'Twitterbot/1.1'],
  [
    'facebook.com',
    'facebookexternalhit/1.1 (+http://www.facebook.com/externalhit_uatext.php)',
  ],
  ['pinterest.com', 'Pinterestbot/1.0 (+http://www.pinterest.com/bot.html)'],
  [
    'bing.com',
    'Mozilla/5.0 (compatible; bingbot/2.0; +http://www.bing.com/bingbot.htm)',
  ],
  ['amazon.com', 'Amazonbot/1.0 (+http://www.amazon.com/bot.html)'],
  ['linkedin.com', 'LinkedInBot/1.0 (+http://www.linkedin.com)'],
  ['x.com', 'X-SnippetBot 1.0'],
  ['whatsapp.com', 'WhatsApp-Business-API-Client'],
  [
    'baidu.com',
    'Mozilla/5.0 (compatible; Baiduspider/2.0; +http://www.baidu.com)',
  ],
  [
    'yahoo.com',
    'Mozilla/5.0 (compatible; Yahoo! Slurp; http://help.yahoo.com)',
  ],
  [
    'netflix.com',
    'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/141.0.0.0 Safari/537.36',
  ],
  [
    'ebay.com',
    'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/141.0.0.0 Safari/537.36',
  ],
  ['tiktok.com', 'TikTok-Crawl/1.0'],
  ['wikipedia.org', `MyPreviewBot/1.0 (${PRODUCTION_HOST}; ${CONTACT_EMAIL})`],
  [
    'microsoft.com',
    'Mozilla/5.0 (compatible; bingbot/2.0; +http://www.bing.com)',
  ],
  [
    'apple.com',
    'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_10_1) AppleWebKit/600.2.5 (KHTML, like Gecko) Version/8.0.2 Safari/600.2.5 (Applebot/0.1; +http://www.apple.com)',
  ],
  ['yandex.ru', 'Mozilla/5.0 (compatible; YandexBot/3.0; +http://yandex.com)'],
  [
    'instagram.com',
    'meta-externalfetcher/1.1 (+http://www.instagram.com/externalfetcher.html)',
  ],
])

export function getDomainUserAgent(targetUrl: string): string {
  let retVal: string =
    'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36'
  try {
    for (const [domain, userAgent] of USER_AGENT_BY_DOMAIN) {
      if (targetUrl.includes(domain)) return userAgent
    }
  } catch (e) {
    if (S_DEBUG_MODE) console.log(e)
  }
  return retVal
}
export const getUrlFallbackName = (urlStr: string): string => {
  try {
    const url = new URL(urlStr)
    let host = url.hostname.toLowerCase()
    if (host.startsWith('www.')) host = host.slice(4)
    const brand = host.split('.')[0]
    if (brand) return brand.charAt(0).toUpperCase() + brand.slice(1)
    return host
  } catch (e) {
    if (S_DEBUG_MODE) console.log(e)
    return urlStr
  }
}

export async function getWebsitePreview(url: any, lang: any) {
  return new Promise<any>(async (resolve) => {
    let ret: any = null
    try {
      if (isNullOrEmptyString(url)) resolve(ret)
      let isInsecureSite: boolean = false
      if (BLOCK_INSECURE_SITES) {
        try {
          const endpoint = `https://webrisk.googleapis.com/v1/uris:search?uri=${encodeURIComponent(
            url,
          )}&threat_types=SOCIAL_ENGINEERING&threat_types=UNWANTED_SOFTWARE&threat_types=MALWARE&key=${GOOGLE_WEB_RISK_API_KEY}`
          const res = await fetch(endpoint, {
            method: 'GET',
            next: { revalidate: 86400 },
          })
          const data = await res.json()
          isInsecureSite = !!data.threat
        } catch (eza) {
          if (S_DEBUG_MODE) console.log(eza)
        }
      }
      if (!isInsecureSite) {
        const acceptLanguageValue = `${lang}-${translate(
          UNITED_STATES_ISO_3166_ALPHA_2_CODE,
          SPAIN_ISO_3166_ALPHA_2_CODE,
          FRANCE_ISO_3166_ALPHA_2_CODE,
          SOUTH_KOREA_ISO_3166_ALPHA_2_CODE,
          lang,
        )},${translate(
          LANGUAGE_CODE_ENGLISH,
          LANGUAGE_CODE_SPANISH,
          LANGUAGE_CODE_FRENCH,
          LANGUAGE_CODE_KOREAN,
          lang,
        )};q=0.9,en;q=0.8`
        const opts: any = {
          method: 'GET',
          headers: {
            'User-Agent': getDomainUserAgent(url),
            Accept: '*/*',
            'Accept-Language': acceptLanguageValue,
          },
          signal: AbortSignal.timeout(3500),
          next: { revalidate: 86400, tags: ['brand-metadata'] },
        }
        await fetch(url, opts)
          .then(async (res) => {
            let title: string = ''
            let icon: string = ''
            let metaDescription: string = ''
            let metaKeywords: string = ''
            let brandColor: string = ''
            if (res?.status == 200) {
              const { parse } = await import('node-html-parser')
              const html = await res.text()
              const root = parse(html)
              title =
                root.querySelector('title')?.innerText ||
                root
                  .querySelector('meta[property="og:title"]')
                  ?.getAttribute('content') ||
                root.querySelector('h1')?.innerText ||
                new URL(url).hostname
              metaDescription =
                root
                  .querySelector('meta[name="description"]')
                  ?.getAttribute('content') ||
                root
                  .querySelector('meta[property="og:description"]')
                  ?.getAttribute('content') ||
                ''
              metaKeywords =
                root
                  .querySelector('meta[name="keywords"]')
                  ?.getAttribute('content') || ''
              brandColor =
                root
                  .querySelector('meta[name="theme-color"]')
                  ?.getAttribute('content') || ''
              const rawIcon =
                root.querySelector('link[rel*="icon"]')?.getAttribute('href') ||
                root
                  .querySelector('link[rel="apple-touch-icon"]')
                  ?.getAttribute('href')
              root
                .querySelector('link[rel="icon"][sizes="192x192"]')
                ?.getAttribute('href')
              ;`${new URL(url).origin}/favicon.ico`

              icon = resolveIconUrl(rawIcon, url)
            }
            ret = {
              title: isNotNullOrEmptyString(title)
                ? toTrimmedString(title)
                : getUrlFallbackName(url),
              icon: toTrimmedString(icon),
              metaDescription: metaDescription,
              metaKeywords: metaKeywords,
              brandColor: brandColor,
            }
            resolve(ret)
          })
          .catch((ez: any) => {
            if (S_DEBUG_MODE) console.log(ez)
            throw ez
          })
      } else {
        resolve(ret)
      }
    } catch (e) {
      if (S_DEBUG_MODE) console.log(e)
      resolve(ret)
    }
  })
}

export function getHowToStepDescriptor(step: any, selectedLanguage: any) {
  return step == 2
    ? translate(
        'Provide a brief description of the mood or vibe that you want to create with music in your business space.',
        'Proporcione una breve descripción del ambiente o la atmósfera que desea crear con la música en su espacio de trabajo.',
        "Décrivez brièvement l'ambiance ou l'atmosphère que vous souhaitez créer avec la musique dans votre espace commercial.",
        '귀사의 사업 공간에서 음악을 통해 어떤 분위기나 느낌을 연출하고 싶은지 간략하게 설명해 주세요.',
        selectedLanguage,
      )
    : step == 1
      ? translate(
          "Our AI analyzes your website content to align the music's energy with your brand identity and visual aesthetic.",
          'Nuestra inteligencia artificial analiza el contenido de su sitio web para alinear la energía de la música con la identidad de su marca y su estética visual.',
          "Notre intelligence artificielle analyse le contenu de votre site web afin d'harmoniser l'énergie de la musique avec l'identité de votre marque et votre esthétique visuelle.",
          '저희 인공지능은 귀하의 웹사이트 콘텐츠를 분석하여 음악의 분위기를 브랜드 아이덴티티 및 시각적 미학과 조화롭게 만들어 줍니다.',
          selectedLanguage,
        )
      : translate(
          "Click the button. A playlist will be generated that suits the atmosphere of your workspace, based on your input and the website's overall mood.",
          'Haz clic en el botón. Se generará una lista de reproducción que se adapte al ambiente de tu espacio de trabajo, basándose en tu indicación y en la atmósfera del sitio web.',
          "Cliquez sur le bouton. Une liste de lecture sera générée, adaptée à l'ambiance de votre espace de travail, en fonction de vos préférences et de l'atmosphère générale du site web.",
          '버튼을 클릭하세요. 여러분의 입력 내용과 웹사이트의 전반적인 분위기를 바탕으로 작업 공간에 어울리는 플레이리스트가 생성됩니다.',
          selectedLanguage,
        )
}
